import { makeVar } from '@apollo/client'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { AuthUserDetails, UserRoleEnum, UsersWorkspace } from '../types/user'
import type { IMyWorkspace } from '../types/workspace'
import { tokenUtils } from '../utils/token-utils'

// Apollo reactive variables
export const selectedWorkspaceVar = makeVar<IMyWorkspace | null>(null)

// Separate store for selectedWorkspace using localStorage
export const useSelectedWorkspaceStore = create<{
  selectedWorkspace: UsersWorkspace | null
  setSelectedWorkspace: (workspace: UsersWorkspace | null) => void
}>()(
  persist(
    (set) => ({
      selectedWorkspace: (tokenUtils.getDataFromLocalStore('selectedWorkspace') as UsersWorkspace | null) || null,
      setSelectedWorkspace: (workspace) => {
        set({ selectedWorkspace: workspace })
        selectedWorkspaceVar(workspace?.workspace || null)
      }
    }),
    {
      name: 'selected-workspace-storage',
      storage: createJSONStorage(() => localStorage)
    }
  )
)

interface AuthState {
  authUserDetails: AuthUserDetails | null
  loadingUserDetails: boolean
  showConfetti: boolean
  signedUrl: string | null
}

interface AuthActions {
  clearAuth: () => void
  setAuthUserDetails: (details: AuthUserDetails | null) => void
  setLoadingUserDetails: (loading: boolean) => void
  setShowConfetti: (show: boolean) => void
  setSignedUrl: (url: string | null) => void
}

interface AuthComputed {
  isUserAdminOrOwner: (role: UserRoleEnum) => boolean
}

type AuthStore = AuthState & AuthActions & AuthComputed

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      authUserDetails: null,
      clearAuth: () => {
        set({
          authUserDetails: null
        })
        selectedWorkspaceVar(null)
      },
      isUserAdminOrOwner: (role: UserRoleEnum) => ['Admin', 'Owner'].includes(role),
      loadingUserDetails: false,
      setAuthUserDetails: (details) => {
        set({ authUserDetails: details })
      },
      setLoadingUserDetails: (loading) => {
        set({ loadingUserDetails: loading })
      },
      setShowConfetti: (show) => {
        set({ showConfetti: show })
      },
      setSignedUrl: (url) => {
        set({ signedUrl: url })
      },
      showConfetti: false,
      signedUrl: null
    }),
    {
      name: 'auth-storage',
      // Only persist these fields
      partialize: (state) => ({
        authUserDetails: state.authUserDetails
      })
    }
  )
)
